import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, ElementRef, inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LeadApi } from '../../api/leadApi';
import { EducationalInstitution } from '../../models/educational_institution';
import { debounceTime, map, Observable, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { SponsorDialogComponent } from '../sponsor-dialog/sponsor-dialog.component';
import { ErrorDialogComponent } from '../error-dialog/error-dialog.component';
import { WizardService } from '../../services/wizard.service';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { ActivatedRoute } from '@angular/router';
import { ReCaptchaV3Service } from 'ng-recaptcha-2';
import { ErrorConstants } from '../../../constants/error_constants';
import { Router } from "@angular/router";
import { environment } from '../../../environments/environment';

export function RequireMatch(control: AbstractControl) {
  const selection: any = control.value;
  if (typeof selection === "string") {
    return { incorrect: true };
  }
  return null;
}

@Component({
    selector: 'app-application',
    templateUrl: './application.component.html',
    styleUrl: './application.component.scss',
    encapsulation: ViewEncapsulation.ShadowDom,
    animations: [
        trigger('fadeInOut', [
            state('void', style({ opacity: 0 })),
            transition(':enter', [animate('600ms ease-in')]),
            transition(':leave', [animate('0ms ease-out')]),
        ]),
    ],
    standalone: false
})
export class ApplicationComponent implements OnInit, OnDestroy {
  public studentForm!: FormGroup;
  public sponsorForm!: FormGroup;
  public educationalInstitutions: EducationalInstitution[] = [];
  public filteredEIS!: Observable<EducationalInstitution[]> | undefined;
  public filter_partner_school: boolean = false;
  public is_loading: boolean = true;
  public showSponsorDetails: boolean = false;
  public showQuestion: boolean = false;
  public is_ei_unique_link: boolean = false;

  public isEIGroup: boolean = false;

  public eiLoading: boolean = false;

  public canClickNext: boolean = false;

  public isEarnMore: string | undefined;

  @ViewChild('educationalInstitution') educationalInstitution: ElementRef | undefined;

  destroy$: Subject<boolean> = new Subject<boolean>();


  readonly dialog = inject(MatDialog);

  public isRecommendedInstitution: boolean = false;

  public isTermsAccepted: boolean = false;
  public isTerms2Accepted: boolean = false;

  public markAsError: boolean = false;

  private lead_id: any = null;
  private backgroundLeadCreated: boolean = true;


  @ViewChild(MatAutocompleteTrigger)
  autocomplete!: MatAutocompleteTrigger;
  utm_source: string = '';


  constructor(
    private fb: FormBuilder,
    private leadApi: LeadApi,
    public wizardService: WizardService,
    private route: ActivatedRoute,
    private recaptchaV3Service: ReCaptchaV3Service,
    private router: Router,
  ) {
    this.studentForm = this.fb.group({
      student_name: ['', Validators.required],
      student_surname: ['', Validators.required],
      student_id_number: ['', [Validators.required, this.wizardService.verifyid_number]],
      student_email: ['', [Validators.required, Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/)]],
      student_contact_number: ['', [Validators.required, Validators.pattern(/^(\+?\d{1,3}[- ]?)?\d{10}$/)]],
      educational_institution: ['', [Validators.required, RequireMatch]],
      educational_institution_name: [],
      educational_institution_contact_email: ['', Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/)],
      educational_institution_contact_number: ['', Validators.pattern(/^(\+?\d{1,3}[- ]?)?\d{10}$/)],
      educational_institution_website: [],
      student_number: [''],
      requested_amount: [null, [Validators.required, Validators.min(2000), Validators.max(400000)]],
      paymentMadeBy: [null, Validators.required],
    });

    this.sponsorForm = this.fb.group({
      sponsor_name: ['', Validators.required],
      sponsor_surname: ['', Validators.required],
      sponsor_id_number: ['', [Validators.required, this.wizardService.verifyid_number]],
      sponsor_email: ['', [Validators.required, Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/)]],
      sponsor_contact_number: ['', [Validators.required, Validators.pattern(/^(\+?\d{1,3}[- ]?)?\d{10}$/)]],
    });
  }

  ngOnInit() {
    // Educational Institution
    const unique_id = this.route.snapshot.paramMap.get('unique_id') || sessionStorage.getItem('unique_id');
    if (unique_id) {
      sessionStorage.setItem('unique_id', unique_id);
      this.getUniqueLinkEducationalInstitution(unique_id);
    }

    this.filteredEIS = this.studentForm.get('educational_institution')!.valueChanges.pipe(
      debounceTime(1000),
      startWith(''),
      switchMap(val => {
        return this._filter(val || '');
      })
    );


    this.utm_source = this.route.snapshot.queryParamMap.get('utm_source') || sessionStorage.getItem('utm_source') || '';

    sessionStorage.setItem('utm_source', this.utm_source);


    if (sessionStorage.getItem('lead_id')) {
      this.lead_id = sessionStorage.getItem('lead_id');
    }

    this.studentForm.get('paymentMadeBy')?.valueChanges.subscribe(value => {
      this.handlePaymentChange(value);
    });
    this.is_loading = false;
  }

  private _filter(value: string): Observable<any[]> {
    if (typeof value !== 'string') {
      return new Observable<any[]>();
    }

    return this.leadApi.searchEducationalInstitutions(value, this.filter_partner_school)
      .pipe(
        takeUntil(this.destroy$),
        map((res: any) => {
          if (res.data.length === 0) {
            this.educationalInstitutions = [{ id: 0, education_group: { id: 0, unique_id: 0, name: '' }, unique_id: '0', ei_unique_id: '0', name: 'No results found', contact_email: '', recommended_institution: false, contact_number: '', website: '' }];
          } else if (res.success) {
            this.educationalInstitutions = res.data;
          }
          else {
            console.error('Error fetching educational institutions:', res.message);
          }
          this.eiLoading = false;
          return this.educationalInstitutions;
        })
      );
  }

  // Unique link single EI
  private getUniqueLinkEducationalInstitution(ei_unique_id: any) {
    this.leadApi.getUniqueLinkEducationalInstitution(ei_unique_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res.success) {
            this.studentForm.patchValue({
              educational_institution: res.data
            });
            this.studentForm.get('educational_institution')?.disable();
            this.is_ei_unique_link = true;
          } else {
            this.leadApi.getEducationalInstitutionsFromGroup(ei_unique_id)
              .pipe(takeUntil(this.destroy$))
              .subscribe((res: any) => {
                if (res.data.length > 0) {
                  this.educationalInstitutions = res.data;
                  this.isEIGroup = true;
                }
              });
          }
        },
        error: (error) => {
          console.error('Error fetching educational institution:', error);
        },
      });
  };

  openSponsorDialog() {
    this.is_loading = true;

    if (this.formsValid()) {
      if (this.isEarnMore === 'yes') {
        try {

          // Clean lead_id to remove accidental extra characters
          if (this.lead_id) {
            this.lead_id = this.lead_id.toString().split('https')[0]; // remove accidental "https" appended
          }

          let redirectUrl = `https://nedbankeducation.co.za/completed/${this.lead_id}`;
          if (environment.env_name === 'dev') {
            redirectUrl = `https://dev.nedbank.smartfunder.co.za/completed/${this.lead_id}`;
          } else if (environment.env_name === 'local') {
            redirectUrl = `http://localhost:4200/completed/${this.lead_id}`;
          }

          this.recaptchaV3Service.execute('CreatingLead')
            .pipe(takeUntil(this.destroy$))
            .subscribe((token: any) => {

              const leadData = {
                student: this.getStudentData(),
                educational_institution: this.isRecommendedInstitution ? this.getRecommendedEI() : this.studentForm.get('educational_institution')?.value,
                applicant: this.getApplicationData(),
                student_responsible_for_payment: this.studentForm.get('paymentMadeBy')?.value,
                requested_amount: this.studentForm.get('requested_amount')?.value,
                utm_source: this.utm_source,
                recaptcha_token: token,
                terms_of_use_accepted: this.isTermsAccepted,
                openSponsorDialog: true,
              }

              if (this.lead_id) {
                this.leadApi.updateLead(leadData, this.lead_id)
                  .pipe(takeUntil(this.destroy$))
                  .subscribe((response: any) => {
                    if (response.success) {
                      console.log('Lead updated successfully');
                      sessionStorage.clear();
                      const dialogRef = this.dialog.open(SponsorDialogComponent, {
                        data: {
                          lead: response.data,
                          redirectUrl: redirectUrl,
                          recaptcha_token: token,
                        }
                      });

                      dialogRef.afterClosed()
                        .pipe(takeUntil(this.destroy$))
                        .subscribe(result => {
                          console.log(`Dialog result: ${result}`);
                        });
                    } else {
                      console.log('Lead update failed');
                    }
                    this.is_loading = false
                  });
              }
              else {
                this.leadApi.createLead(leadData)
                  .pipe(takeUntil(this.destroy$))
                  .subscribe((response: any) => {
                    if (response.success) {
                      console.log('Lead created successfully');
                      this.lead_id = response.data.identifier;
                      sessionStorage.setItem('lead_id', response.data.identifier);
                      const dialogRef = this.dialog.open(SponsorDialogComponent, {
                        data: {
                          lead: response.data,
                          redirectUrl: redirectUrl,
                          recaptcha_token: token
                        }
                      });

                      dialogRef.afterClosed()
                        .pipe(takeUntil(this.destroy$))
                        .subscribe(result => {
                          console.log(`Dialog result: ${result}`);
                        });

                    } else {
                      console.log('Lead creation failed');
                    }
                    this.is_loading = false;
                  });
              }
            },
              (error) => {
                this.is_loading = false;
                console.log('Error creating lead', error);
                this.openErrorDialog('Error', 'An error occurred. Please try again later.', ErrorConstants.RECAPTCHA);
              }
            );

        } catch (error) {
          this.is_loading = false;
          console.log('Error creating lead', error);
          this.openErrorDialog('Error', 'An error occurred. Please try again later.', ErrorConstants.SEND_LINK_EMAIL);
        }

      } else {
        this.is_loading = false;
        this.updateLeadStatus();

      }
    } else {
      this.studentForm.markAllAsTouched();
      this.sponsorForm.markAllAsTouched();
      this.validateTerms();
      console.log('Forms are invalid');
      this.is_loading = false;
    }

  }

  validateTerms() {
    this.markAsError = !this.isTermsAccepted;
    this.markAsError = !this.isTerms2Accepted;

  }

  openErrorDialog(title: string, message: string, errorCode: string) {
    const dialogRef = this.dialog.open(ErrorDialogComponent, {
      data: {
        title: title,
        message: message,
        errorCode: errorCode
      }
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        console.log(`Dialog result: ${result}`);
      });
  }

  validateFormControl(controlName: string, form: string) {
    if (form === 'student') {
      return this.studentForm.get(controlName)?.invalid && this.studentForm.get(controlName)?.touched;
    } else if (form === 'sponsor') {
      return this.sponsorForm.get(controlName)?.invalid && this.sponsorForm.get(controlName)?.touched;
    } else {
      return false;
    }
  }

  validateNumericInput(event: KeyboardEvent): void {
    const charCode = event.charCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }

  scrollToInstitution(): void {
    if (this.educationalInstitution && window.innerWidth <= 800) {
      this.educationalInstitution.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  validationMessage(controlName: string, form: string): string {

    let control;
    if (form === 'student') {
      control = this.studentForm.get(controlName);
    } else if (form === 'sponsor') {
      control = this.sponsorForm.get(controlName);
    }

    if (controlName === 'sponsor_id_number' || controlName === 'student_id_number') {
      if (control?.hasError('id_number')) {
        return 'Enter a valid 13 digit South African ID number'
      }
    }

    if (control?.hasError('required')) {
      return 'This field is required';
    }


    if (controlName === 'sponsor_email' || controlName === 'student_email' || controlName === 'educational_institution_contact_email') {
      if (control?.hasError('pattern')) {
        return 'Enter a valid email address';
      }
    }

    if (control?.hasError('minlength')) {
      return 'This field must be 13 characters';
    }

    if (control?.hasError('maxlength')) {
      return 'This field must be 13 characters';
    }

    if (control?.hasError('pattern')) {
      return 'Enter a valid 10 digit contact number, for example 0823334444';
    }

    if (control?.hasError('incorrect')) {
      return 'Please select an educational institution from the list';
    }

    if (control?.hasError('min')) {
      return 'Enter an amount above R2 000';
    }

    if (control?.hasError('max')) {
      return 'Enter an amount up to R400 000';
    }

    return 'This field is required';
  }


  paymentMadeByChanged(event: any) {
    // Directly call the handler
    this.handlePaymentChange(event.value);
  }

  private handlePaymentChange(value: string) {
    if (value === 'sponsor') {
      this.showSponsorDetails = true;
      // Reset only sponsor form controls
      this.sponsorForm.reset();
      this.studentForm.get('paymentMadeBy')?.setValue('sponsor', { emitEvent: false });
    } else {
      this.showSponsorDetails = false;
      this.studentForm.get('paymentMadeBy')?.setValue('student', { emitEvent: false });
    }

    if (this.backgroundLeadCreated) {
      this.createBackgroundLead();
      this.backgroundLeadCreated = false;
    }

    this.showQuestion = true;
  }

  getEiName(ei: any): string {
    return ei ? ei.name : '';
  }

  formsValid(): boolean {
    // Student form must be valid
    const studentValid = this.studentForm.valid;

    // Sponsor form is required only if showSponsorDetails is true
    const sponsorValid = !this.showSponsorDetails || this.sponsorForm.valid;

    // Terms must be accepted
    const termsValid = this.isTermsAccepted && this.isTerms2Accepted;

    // Return true if everything is valid
    return studentValid && sponsorValid && termsValid;
  }


  getStudentData(): any {
    return {
      first_name: this.studentForm.get('student_name')?.value,
      last_name: this.studentForm.get('student_surname')?.value,
      id_number: this.studentForm.get('student_id_number')?.value,
      email: this.studentForm.get('student_email')?.value,
      contact_number: this.studentForm.get('student_contact_number')?.value,
      student_number: this.studentForm.get('student_number')?.value,
    }
  }

  getApplicationData(): any {
    if (this.showSponsorDetails) {
      return {
        first_name: this.sponsorForm.get('sponsor_name')?.value,
        last_name: this.sponsorForm.get('sponsor_surname')?.value,
        id_number: this.sponsorForm.get('sponsor_id_number')?.value,
        email: this.sponsorForm.get('sponsor_email')?.value,
        contact_number: this.sponsorForm.get('sponsor_contact_number')?.value,
      }
    } else {
      return this.getStudentData();
    }
  }

  createBackgroundLead() {
    if (this.studentForm.valid && !this.lead_id) {
      try {

        this.recaptchaV3Service.execute('CreatingLead')
          .pipe(takeUntil(this.destroy$))
          .subscribe((token: any) => {

            const leadData = {
              student: this.getStudentData(),
              educational_institution: this.isRecommendedInstitution ? this.getRecommendedEI() : this.studentForm.get('educational_institution')?.value,
              applicant: undefined,
              student_responsible_for_payment: this.studentForm.get('paymentMadeBy')?.value,
              requested_amount: this.studentForm.get('requested_amount')?.value,
              utm_source: this.utm_source,
              recaptcha_token: token,
              terms_of_use_accepted: this.isTermsAccepted,
            }

            this.leadApi.createLead(leadData)
              .pipe(takeUntil(this.destroy$))
              .subscribe((response: any) => {
                if (response.success) {
                  this.lead_id = response.data.identifier;
                  sessionStorage.setItem('lead_id', response.data.identifier);
                } else {
                  this.openErrorDialog('Error', 'An error occurred. Please try again later.', ErrorConstants.CREATE_LEAD);
                }
                this.canClickNext = true;
                this.is_loading = false;
              });

          }, (error) => {
            this.is_loading = false;
            console.log('Error creating lead', error);
            this.openErrorDialog('Error', 'An error occurred. Please try again later.', ErrorConstants.RECAPTCHA);
          });
      } catch (error) {
        this.is_loading = false;
        console.log('Error creating lead', error);
        this.openErrorDialog('Error', 'An error occurred. Please try again later.', ErrorConstants.CREATE_LEAD);
      }
      finally {
        this.is_loading = false;
      }
    } else if (this.lead_id) {
      this.canClickNext = true;
    }
  }

  createLead() {
    this.is_loading = true;
    if (this.formsValid()) {
      try {

        this.recaptchaV3Service.execute('CreatingLead')
          .pipe(takeUntil(this.destroy$))
          .subscribe((token: any) => {

            const leadData = {
              student: this.getStudentData(),
              educational_institution: this.isRecommendedInstitution ? this.getRecommendedEI() : this.studentForm.get('educational_institution')?.value,
              applicant: this.getApplicationData(),
              student_responsible_for_payment: this.studentForm.get('paymentMadeBy')?.value,
              requested_amount: this.studentForm.get('requested_amount')?.value,
              utm_source: this.utm_source,
              recaptcha_token: token,
              terms_of_use_accepted: this.isTermsAccepted
            }

            this.leadApi.createLead(leadData)
              .pipe(takeUntil(this.destroy$))
              .subscribe((response: any) => {
                if (response.success) {
                  this.lead_id = response.data.identifier;
                  sessionStorage.setItem('lead_id', response.data.identifier);
                  // Redirect to success page
                  sessionStorage.clear();
                  this.router.navigate(['/completed']);
                } else {
                  console.log('Lead creation failed');
                  this.is_loading = false;
                }
              });

          }, (error) => {
            this.is_loading = false;
            console.log('Error creating lead', error);
            this.openErrorDialog('Error', 'An error occurred. Please try again later.', ErrorConstants.RECAPTCHA);
          });
      } catch (error) {
        this.is_loading = false;
        console.log('Error creating lead', error);
        this.openErrorDialog('Error', 'An error occurred. Please try again later.', ErrorConstants.CREATE_LEAD);
      }

    } else {
      this.studentForm.markAllAsTouched();
      this.sponsorForm.markAllAsTouched();
      this.validateTerms();
      this.is_loading = false;
    }
  }

  clearValuesForRecommendedEI() {

    this.studentForm.get('educational_institution_name')?.clearValidators();
    this.studentForm.get('educational_institution_contact_email')?.clearValidators();
    this.studentForm.get('educational_institution_contact_number')?.clearValidators();
    this.studentForm.get('educational_institution_website')?.clearValidators();

    this.studentForm.get('educational_institution_name')?.updateValueAndValidity();
    this.studentForm.get('educational_institution_contact_email')?.updateValueAndValidity();
    this.studentForm.get('educational_institution_contact_number')?.updateValueAndValidity();
    this.studentForm.get('educational_institution_website')?.updateValueAndValidity();

    this.studentForm.get('educational_institution_name')?.markAsPristine();
    this.studentForm.get('educational_institution_contact_email')?.markAsPristine();
    this.studentForm.get('educational_institution_contact_number')?.markAsPristine();
    this.studentForm.get('educational_institution_website')?.markAsPristine();

    this.studentForm.get('educational_institution_name')?.markAsUntouched();
    this.studentForm.get('educational_institution_contact_email')?.markAsUntouched();
    this.studentForm.get('educational_institution_contact_number')?.markAsUntouched();
    this.studentForm.get('educational_institution_website')?.markAsUntouched();
  }

  recommendedInstitution() {
    this.autocomplete.closePanel();
    this.isRecommendedInstitution = true;

    this.clearValuesForRecommendedEI();

    this.studentForm.get('educational_institution')?.clearValidators();
    this.studentForm.get('educational_institution')?.updateValueAndValidity();

    this.studentForm.get('educational_institution_name')?.addValidators(Validators.required);
    this.studentForm.get('educational_institution_name')?.updateValueAndValidity();

    this.studentForm.get('educational_institution_contact_email')?.addValidators([Validators.required, Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/)]);
    this.studentForm.get('educational_institution_contact_email')?.updateValueAndValidity();

    this.studentForm.get('educational_institution_contact_number')?.addValidators([Validators.required, Validators.pattern(/^(\+?\d{1,3}[- ]?)?\d{10}$/)]);
    this.studentForm.get('educational_institution_contact_number')?.updateValueAndValidity();

    this.studentForm.get('educational_institution_website')?.addValidators([Validators.required]);
    this.studentForm.get('educational_institution_website')?.updateValueAndValidity();
  }

  getRecommendedEI(): EducationalInstitution {
    return {
      id: 0,
      education_group: { id: 0, unique_id: 0, name: '' },
      unique_id: '0',
      ei_unique_id: '0',
      name: this.studentForm.get('educational_institution_name')?.value,
      contact_email: this.studentForm.get('educational_institution_contact_email')?.value,
      recommended_institution: true,
      contact_number: this.studentForm.get('educational_institution_contact_number')?.value,
      website: this.studentForm.get('educational_institution_website')?.value,
    }
  }

  updateLead() {
    this.is_loading = true;
    if (this.formsValid()) {
      try {
        this.recaptchaV3Service.execute('UpdateLead')
          .pipe(takeUntil(this.destroy$))
          .subscribe((token: any) => {

            const leadData = {
              student: this.getStudentData(),
              educational_institution: this.isRecommendedInstitution ? this.getRecommendedEI() : this.studentForm.get('educational_institution')?.value,
              applicant: this.getApplicationData(),
              student_responsible_for_payment: this.studentForm.get('paymentMadeBy')?.value,
              requested_amount: this.studentForm.get('requested_amount')?.value,
              utm_source: this.utm_source,
              identifier: this.lead_id,
              recaptcha_token: token,
              terms_of_use_accepted: this.isTermsAccepted,
              openSponsorDialog: false,
            }


            this.leadApi.updateLead(leadData, this.lead_id)
              .pipe(takeUntil(this.destroy$))
              .subscribe((response: any) => {
                if (response.success) {
                  sessionStorage.clear();
                  // Redirect to success page
                  this.router.navigate(['/completed']);
                } else {
                  console.log('Lead update failed');
                  this.is_loading = false
                }

              });
          }, (error) => {
            this.is_loading = false;
            console.log('Error updating lead', error);
            this.openErrorDialog('Error', 'An error occurred. Please try again later.', ErrorConstants.RECAPTCHA);
          });
      } catch (error) {
        this.is_loading = false;
        console.log('Error updating lead', error);
        this.openErrorDialog('Error', 'An error occurred. Please try again later.', ErrorConstants.UPDATE_LEAD);
      }
    }
    else {
      this.studentForm.markAllAsTouched();
      this.sponsorForm.markAllAsTouched();
      this.validateTerms();
      this.is_loading = false;
    }
  }

  next() {
    if (!this.canClickNext) {
      return; // Do nothing if button should be disabled
    }

    if (this.isEarnMore === 'yes') {
      if (this.lead_id) {
        console.log('Update lead');
        this.updateLead();
      }
      else {
        console.log('Create lead');
        this.createLead();
      }
    } else {
      this.updateLeadStatus();
    }
  }

  updateLeadStatus() {
    this.is_loading = true;
    if (this.formsValid()) {
      try {
        this.recaptchaV3Service.execute('UpdateLead')
          .pipe(takeUntil(this.destroy$))
          .subscribe((token: any) => {

            const leadData = {
              status_id: 4,
              identifier: this.lead_id,
              recaptcha_token: token,
            }


            this.leadApi.updateLeadStatus(leadData)
              .pipe(takeUntil(this.destroy$))
              .subscribe((response: any) => {
                if (response.success) {
                  sessionStorage.clear();
                  this.router.navigate(['/completed'], { queryParams: { status: 'rejected' } });
                } else {
                  console.log('Lead update failed');
                  this.is_loading = false
                }

              });
          }, (error) => {
            this.is_loading = false;
            console.log('Error updating lead', error);
            this.openErrorDialog('Error', 'An error occurred. Please try again later.', ErrorConstants.RECAPTCHA);
          });
      } catch (error) {
        this.is_loading = false;
        console.log('Error updating lead', error);
        this.openErrorDialog('Error', 'An error occurred. Please try again later.', ErrorConstants.UPDATE_LEAD);
      }
    }
    else {
      this.studentForm.markAllAsTouched();
      this.sponsorForm.markAllAsTouched();
      this.validateTerms();
      this.is_loading = false;
    }
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

}



